import apiClient from "../../api/api";

/**
 * Retrieves the historical rate changes for a specific user.
 *
 * @async
 * @function getUserHistoricalRateRequest
 * @param {number|string} userId - Unique identifier of the user.
 * @returns {Promise<Array<Object>>} List of historical rate records.
 *
 * @example
 * await getUserHistoricalRateRequest(1097);
 */
export const getUserHistoricalRateRequest = async(userId) => {
    const getHistoricalResponse = await apiClient.get(import.meta.env.VITE_GET_USER_RATE+`/${userId}/history`)
    
    return getHistoricalResponse.data
}


/**
 * Retrieves the current rate configuration for a specific user.
 *
 * @async
 * @function getUserRateRequest
 * @param {number|string} userId - Unique identifier of the user.
 * @param {string} search - Search term for filtering results.
 * @returns {Promise<Object>} User rate data returned by the API.
 *
 * @example
 * await getUserRateRequest(1097);
 */
export const getUserRateRequest = async(userId, search = "") => {
    let getUserRateResponse;
    if(search) {
        search = encodeURIComponent(search)
        getUserRateResponse = await apiClient.get(import.meta.env.VITE_GET_USER_RATE+`/?search=${search}`)
    } else {
        getUserRateResponse = await apiClient.get(import.meta.env.VITE_GET_USER_RATE+`/${userId}`)

    }
    
    return getUserRateResponse.data
}

/**
 * Retrieves the current rate configuration for the authenticated user.
 *
 * @async
 * @function requestGetMyRate
 * @returns {Promise<Object>} Authenticated user's rate data returned by the API.
 *
 * @example
 * await requestGetMyRate();
 */
export const requestGetMyRate = async() => {
    const response = await apiClient.get(import.meta.env.VITE_GET_USER_RATE)

    return response.data
}

/**
 * Updates the pay frequency configuration for a specific user.
 *
 * @async
 * @function updateUserPayFrequencyRequest
 * @param {number|string} userId - Unique identifier of the user.
 * @param {Object|string} payFrequency - New pay frequency value or payload expected by the API.
 * @returns {Promise<Object>} Updated user pay frequency data.
 *
 * @example
 * await updateUserPayFrequencyRequest(1097, { payFrequency: "monthly" });
 */
export const updateUserPayFrequencyRequest = async(userId, payFrequency) => {
    const updatePayFrequencyResponse = await apiClient.patch(import.meta.env.VITE_GET_USER_RATE+`/${userId}/pay-frequency`, payFrequency, {successToast:"Pay frequency updated"})
    
    return updatePayFrequencyResponse
}

/**
 * Creates a new rate configuration.
 *
 * @async
 * @function createUserRateRequest
 * @param {number} userId - Unique identifier of the user.
 * @param {Object} rate - Payload containing rate configuration data.
 * @returns {Promise<Object>} Created rate data returned by the API.
 *
 * @example
 * await createUserRateRequest(1097, { rate: 50, payFrequency: "monthly" });
 */
export const createUserRateRequest = async(userId, rate) => {
    const createRateResponse = await apiClient.post(import.meta.env.VITE_BASE_RATE_URL + `/${userId}`, rate, {successToast:"User rate created"})
    
    return createRateResponse
}